<template>
  <div class="modal fade" id="map-styles-dialog" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-body p-0">
          <div class="container">
            <!-- Tabs -->
            <template v-if="!printMapMode">
              <ul class="nav bg-gray mt-3 mb-1 position-relative rounded-pill p-1" id="pills-tab" role="tablist">
                <div class="nav-indicator"></div>
                <li role="presentation">
                  <button
                    class="nav-link rounded-pill active"
                    id="map-modal-maps"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-home"
                    type="button"
                    role="tab"
                    aria-controls="pills-home"
                    aria-selected="true"
                  >
                    Maps
                  </button>
                </li>
                <li role="presentation">
                  <button
                    class="nav-link rounded-pill"
                    id="map-modal-overlays"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-profile"
                    type="button"
                    role="tab"
                    aria-controls="pills-profile"
                    aria-selected="false"
                  >
                    Overlays
                  </button>
                </li>
                <li role="presentation">
                  <button
                    class="nav-link rounded-pill"
                    id="map-modal-pois"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-contact"
                    type="button"
                    role="tab"
                    aria-controls="pills-contact"
                    aria-selected="false"
                  >
                    POIs
                  </button>
                </li>
              </ul>
            </template>

            <!-- Tab content -->
            <div class="tab-content" id="pills-tabContent">
              <!-- Maps Tab -->
              <div
                class="tab-pane fade show active"
                id="pills-home"
                role="tabpanel"
                aria-labelledby="map-modal-maps"
              >
                <div class="row">
                  <!-- Free Styles -->
                  <div class="col-12 px-0 pt-3 pb-2">
                    <p class="px-3 mb-0">
                      <small><strong>Standard maps</strong></small>
                    </p>
                    <div ref="freeScroll" class="mapstyle-list">
                      <div
                        class="position-relative d-inline-block"
                        v-for="(style, index) in freeMapStyles"
                        :key="index"
                        @click="onStyleClick(style)"
                      >
                        <div
                          class="mapstyle-card"
                          :class="{ 'current-style': currentMapStyle && style.name === currentMapStyle.name }"
                        >
                          <img :src="style.image" alt="Map" draggable="false" />
                          <div class="hiking-text p-1">
                            <small>
                              <p class="m-0"><strong>{{ style.name }}</strong></p>
                            </small>
                            <small>
                              <p class="text-muted">{{ style.isPro ? 'PRO+' : 'Free' }}</p>
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- PRO Styles -->
                  <div class="col-12 px-0 py-1 d-block">
                    <p class="px-3 mb-0">
                      <small><strong>PRO+ map collections</strong></small>
                    </p>
                    <div ref="proScroll" class="mapstyle-list">
                      <div
                        class="position-relative d-inline-block"
                        v-for="(style, index) in proMapStyles"
                        :key="index"
                        @click="onStyleClick(style)"
                      >
                        <div
                          :class="[
                            'mapstyle-card',
                            {
                              'cannot-print': printMapMode && !style.isPrintMap,
                              'current-style': currentMapStyle && style.name === currentMapStyle.name,
                            }
                          ]"
                        >
                          <img :src="style.image" alt="Map" draggable="false" />
                          <div class="hiking-text p-1">
                            <small>
                              <p class="m-0"><strong>{{ style.name }}</strong></p>
                            </small>
                            <small>
                              <p class="text-muted">{{ style.isPro ? 'PRO+' : 'Free' }}</p>
                            </small>
                          </div>
                          <a
                            href="https://blog.hiiker.app/2024/03/09/introducing-map-collections/"
                            target="_blank"
                          >
                            <span class="material-icons map-info">info</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <template v-if="!printMapMode">
                <!-- Overlays Tab -->
                <div
                  class="tab-pane fade"
                  id="pills-profile"
                  role="tabpanel"
                  aria-labelledby="map-modal-overlays"
                >
                  <BaseMapDataTrailsTab />
                </div>

                <!-- Base Map POIs tab -->
                <div
                  class="tab-pane fade"
                  id="pills-contact"
                  role="tabpanel"
                  aria-labelledby="map-modal-pois"
                >
                  <BaseMapDataPoIsTab />
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick, onBeforeUnmount } from 'vue';
import { Tab } from 'bootstrap';
import { HiikerMapStyle } from '../../utils/models/MapStyles';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import axiosClient from '../../utils/helpers/axios_client';
import emitter from '../../utils/bus';
import BaseMapDataPoIsTab from "./components/BaseMapDataPOIsTab.vue";
import BaseMapDataOverlaysTab from "./components/BaseMapDataOverlaysTab.vue";

export default defineComponent({
  components: { BaseMapDataTrailsTab: BaseMapDataOverlaysTab, BaseMapDataPoIsTab },

  setup() {
    const freeScroll = ref<HTMLElement | null>(null);
    const proScroll = ref<HTMLElement | null>(null);

    const makeDraggable = (el: HTMLElement) => {
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;
      let moved = false;

      el.addEventListener('mousedown', e => {
        isDown = true;
        moved = false;
        el.classList.add('dragging');
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
      });

      el.addEventListener('mousemove', e => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX) * 1.5;
        if (Math.abs(x - startX) > 5) moved = true;
        el.scrollLeft = scrollLeft - walk;
      });

      ['mouseup', 'mouseleave'].forEach(evt =>
        el.addEventListener(evt, () => {
          isDown = false;
          el.classList.remove('dragging');
        })
      );

      el.addEventListener('click', e => {
        if (moved) e.stopImmediatePropagation();
      }, true);
    };

    nextTick(() => {
      if (freeScroll.value) makeDraggable(freeScroll.value);
      if (proScroll.value) makeDraggable(proScroll.value);
    });

    const nav = ref<HTMLElement | null>(null);
    const indicator = ref<HTMLElement | null>(null);
    // const activeTabId = ref('map-modal-maps'); // Default to the first tab

    const moveIndicator = (el: HTMLElement) => {
      if (!nav.value || !indicator.value || !el) return;
      const navRect = nav.value.getBoundingClientRect();
      const btnRect = el.getBoundingClientRect();
      indicator.value.style.width = `${btnRect.width}px`;
      indicator.value.style.height = `${btnRect.height}px`;
      indicator.value.style.left = `${btnRect.left - navRect.left}px`;
      indicator.value.style.top = `${btnRect.top - navRect.top}px`;
    };

    const setActiveIndicator = () => {
      if (!nav.value) return;
      const activeTab = nav.value.querySelector<HTMLButtonElement>('.nav-link.active');
      if (activeTab) moveIndicator(activeTab);
    };

    const handleResize = () => {
      setActiveIndicator();
    };

    window.addEventListener('resize', handleResize);

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    return { freeScroll, proScroll, nav, indicator, moveIndicator, setActiveIndicator }; // Expose the functions!
  },

  data() {
    return {
      mapStyles: [] as HiikerMapStyle[],
      is3DEnabled: false,
      isInitialized: false,
    };
  },

  async beforeCreate() {
    emitter.on('user:updated', async () => {
      const response = await axiosClient.get('/user/current');
      if (response.data) {
        this.user = response.data;
        window.user = this.user;
      }
    });
  },

  async mounted() {
    this.mapStyles = HiikerMapStyle.getAllStyles();
    const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
    const current = HiikerMapStyle.getCurrentStyle(isProOrProPlus);
    if (current) this.$store.commit('mapStyles/setCurrentMapStyle', current);

    nextTick(() => {
      this.nav = document.getElementById('pills-tab')!;
      this.indicator = this.nav.querySelector<HTMLDivElement>('.nav-indicator')!;

      const modalEl = document.getElementById('map-styles-dialog')!;

      modalEl.addEventListener('shown.bs.modal', () => {
        if (this.isInitialized) return;
        const firstBtn = document.getElementById('map-modal-maps')!;
        Tab.getOrCreateInstance(firstBtn).show();
        this.moveIndicator(firstBtn);
        this.isInitialized = true
      });

      this.nav.addEventListener('shown.bs.tab', (e: Event) => {
        this.moveIndicator(e.target as HTMLElement);
      });

      this.setActiveIndicator();
    });
  },

  methods: {
    user() {
      return window.user?.firebase_user_id ? window.user : null;
    },
    async onStyleClick(style: HiikerMapStyle) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      if (this.printMapMode && !style.isPrintMap) {
        alert('This collection is not available for printing yet...');
        return;
      }
      if (!style.isPro || (window.user && isProOrProPlus)) {
        this.$store.commit('mapStyles/setCurrentMapStyle', style);
        if (style.canBeSaved()) {
          style.saveCurrentStyle();
        }
        window.map.once('style.load', () => emitter.emit('map-style-loaded'));
        const url = this.$store.state.mapStyles.printMapMode
          ? style.printMapUrl()
          : style.styleUrl;
        window.map.setStyle(url);
        setTimeout(() => emitter.emit('map-style-loaded'), 2500);
        this.is3DEnabled = false;
      } else {
        window.location.href = '/purchases';
      }
    }
  },

  computed: {
    currentMapStyle() {
      return this.$store.state.mapStyles.currentMapStyle;
    },
    freeMapStyles() {
      return this.mapStyles.filter(s => !s.isPro);
    },
    proMapStyles() {
      return this.mapStyles.filter(s => s.isPro);
    },
    printMapMode() {
      return this.$store.state.mapStyles.printMapMode;
    }
  },

  watch: {
    is3DEnabled(newVal: boolean) {
      const map = window.map;
      if (newVal) {
        if (!map.getSource('mapbox-dem')) {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14
          });
        }
        if (!map.getTerrain()) {
          map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
        }
        map.setPitch(80);
      } else {
        if (map.getTerrain()) map.setTerrain(null);
        map.setPitch(0);
      }
    }
  }
});
</script>

<style scoped>
.modal-dialog {
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.modal-body {
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  border-top: 1px solid #434343;
}

/* Tabs container */
#pills-tab {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  max-width: 380px;
  margin: auto;
}
#pills-tab > li {
  flex: 1 1 0;
  display: flex;
}
#pills-tab .nav-link {
  flex: 1 1 0;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
  font-weight: 600;
}
#pills-tab .nav-link.active {
  color: var(--dark);
}
.nav-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  background-color: #fddf00;
  border-radius: 9999px;
  transition: left 0.3s ease, width 0.3s ease, top 0.3s ease, height 0.3s ease;
  z-index: 0;
}

/* Horizontal scrolling list */
.mapstyle-list {
  display: flex;
  overflow-x: auto;
  padding-bottom: 1em;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;
}
.mapstyle-list::-webkit-scrollbar {
  display: none;
}
.mapstyle-list.dragging {
  cursor: grabbing;
}
.mapstyle-list > .position-relative {
  flex: 0 0 auto;
  margin-right: 0;
}
.mapstyle-list > .position-relative:first-child {
  margin-left: 1.5em;
}
.mapstyle-list::-webkit-scrollbar {
  display: none;                 /* Chrome, Safari, Opera */
}

.mapstyle-card {
  position: relative;
  width: 120px;
  background-color: #fff;
  border: 2px solid #434343;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  margin-top: 1em;
  margin-right: 1em;
}
.mapstyle-card img {
  width: 100%;
  height: auto;
}
.mapstyle-card .hiking-text {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.7);
}
.mapstyle-card .map-info {
  position: absolute;
  z-index: 100;
  top: 6px;
  left: 6px;
  background: #17181D;
  border-radius: 100px;
}
.current-style {
  border: 2px solid #fddf00;
}
.cannot-print {
  opacity: 0.5;
}

@media (max-width: 475px) {
  .mapstyle-card {
    width: 108px;
    margin-right: 0.7em;
  }
  .map-info {
    font-size: 19px;
  }
}

</style>
